import React, { FC, useEffect, useMemo, useState } from 'react';
import { ViewApI } from 'api/views';
import { View, ViewType } from 'api/views/types';
import {
  getViewTranformer,
  SavedViews as ViewsComponent,
  Views as ViewsT,
} from 'components/molecules/views';
import { empty, load, loading } from 'components/molecules/views/core';
import { useViewActions } from 'components/molecules/views/view-actions';
import { UsersGroupedByCompanyComponents } from 'components/organisms/filter-editors';
import { UserEditorComponents } from 'components/organisms/tasks/criteria/editor-components';
import {
  FilterableField,
  GroupedOptions,
} from 'components/organisms/tasks/fields';
import {
  GroupedFilter,
  TaskGroupedFilterPopover,
} from 'components/organisms/tasks/grouped-filter-popover';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { GroupBy } from '../types';
import { DEFAULT_VIEW_ID } from './default-views';
import {
  ApiResourceManagementViewDefinition,
  ResourceManagementViewDefinition,
  ResourceManagmentViewType,
} from './types';
import { isValidViewDefinition, viewDefinitionComparator } from './utils';
import { getViewDefinitionTransformer } from './view-definition-transformer';

interface Props {
  lookup: GroupFieldsMeta;
  isFieldsLoading: boolean;
  currentDefinition: ResourceManagementViewDefinition;
  currentViewId: number;
  fields: GroupedOptions<FilterableField>[];
  defaultViews: View<ResourceManagementViewDefinition>[];
  onViewSelect: (view: View<ResourceManagementViewDefinition>) => void;
  onFilterChange: (filter: GroupedFilter) => void;
  groupBy: GroupBy;
}
const getSelectEditorComponents = (field) => {
  if (field.fieldName === 'customer') {
    return UserEditorComponents;
  }

  if (field.fieldName === 'teamMembers' || field.fieldName === 'userId') {
    return UsersGroupedByCompanyComponents;
  }

  return undefined;
};

export const Views: FC<Props> = ({
  lookup,
  isFieldsLoading,
  currentDefinition,
  onViewSelect,
  onFilterChange,
  defaultViews,
  fields,
  currentViewId,
  groupBy,
}) => {
  const view = useMemo(
    () =>
      groupBy === GroupBy.Users
        ? ResourceManagmentViewType.Users
        : ResourceManagmentViewType.Projects,
    [groupBy],
  );
  const defaultFilterGroup = useMemo(
    () =>
      groupBy === GroupBy.Users
        ? FilterGroupType.User
        : FilterGroupType.Project,
    [groupBy],
  );
  const viewDefinitionTransformer = useMemo(
    () => getViewDefinitionTransformer(lookup, defaultFilterGroup, view),
    [defaultFilterGroup, lookup, view],
  );
  const viewTransformer = useMemo(
    () => getViewTranformer(viewDefinitionTransformer),
    [viewDefinitionTransformer],
  );
  // :TODO remove the following and move it to query
  const [views, setViews] =
    useState<ViewsT<ResourceManagementViewDefinition>>(empty);
  const [viewApiInitialized, setViewApiInitialized] = useState(false);

  useEffect(() => {
    if (isFieldsLoading || viewApiInitialized) {
      return;
    }
    setViewApiInitialized(true);
    setViews(loading());
    ViewApI.getAllViews<ApiResourceManagementViewDefinition>(
      ViewType.ResourceManagement,
    )
      .then((data) => {
        setViews(
          load(
            data
              .filter(
                ({
                  viewDefinition: {
                    allocations: { view: viewType },
                  },
                }) => view === viewType,
              )
              .map(viewTransformer.from),
          ),
        );
      })
      .catch(() => {
        setViews(empty);
      });
  }, [isFieldsLoading, view, viewApiInitialized, viewTransformer.from]);

  const { onViewCreate, onViewUpdate, onViewDelete } = useViewActions({
    views,
    setViews,
    viewType: ViewType.ResourceManagement,
    transformer: viewDefinitionTransformer,
  });

  return (
    <ViewsComponent
      filterComponent={
        <TaskGroupedFilterPopover
          filter={currentDefinition.filter}
          fields={fields}
          loading={isFieldsLoading}
          onChange={onFilterChange}
          hideClearFilter
          reset={() => {}}
          getSelectEditorComponents={getSelectEditorComponents}
        />
      }
      views={views}
      defaultViewsList={load(defaultViews)}
      defaultViewId={DEFAULT_VIEW_ID}
      currentViewDefinition={currentDefinition}
      onViewSelect={onViewSelect}
      isValidViewDefinition={isValidViewDefinition}
      viewDefinitionComparator={viewDefinitionComparator}
      onViewCreate={onViewCreate}
      onViewDelete={onViewDelete}
      onViewUpdate={onViewUpdate}
      selectedViewId={currentViewId}
    />
  );
};
